import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import {
  entityConfig,
  removeEntities,
  setAllEntities,
  upsertEntities,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import { ContentParser } from '@thiememeulenhoff/js-sdk';
import { ContentService } from '@thiememeulenhoff/ng-api-gateway';
import { translate } from '@thiememeulenhoff/translations';
import {
  Arrangement,
  ArrangementResource,
  ArrangementType,
  Cluster,
  ClusterWithArrangements,
  ContentEntry,
  ContentItem,
  Method,
  MethodDetail,
  RecentScoreItem,
  Resource,
  ResourceType,
} from '@thiememeulenhoff/types';
import { keyBy } from 'lodash-es';
import { flattenResources } from '../_utils/flatten-resources';
import { withRequestStatus } from '../_utils/request-status.feature';
import { collectQuestionsByParentId } from './content.facade.util';

const NT2_SUBJECT = 'Nederlands als tweede taal';

const resourcesConfig = entityConfig({
  entity: {} as Resource,
  collection: 'resources',
  selectId: (e: Resource) => e.contentIdentifier,
});

const contentConfig = entityConfig({
  entity: {} as ContentEntry,
  collection: 'content',
  selectId: (e: ContentEntry) => e.contentIdentifier,
});

const methodsConfig = entityConfig({
  entity: {} as Method,
  collection: 'methods',
  selectId: (e: Method) => e.slug,
});

const clustersWithArrangementsConfig = entityConfig({
  entity: {} as ClusterWithArrangements,
  collection: 'clustersWithArrangements',
  selectId: (e: ClusterWithArrangements) => e.code,
});

const arrangementsConfig = entityConfig({
  entity: {} as Arrangement,
  collection: 'arrangements',
  selectId: (e: Arrangement) => e.code,
});

/**
 * The arrangement resource tree saved by the arrangement code
 */
const arrangementResourceByCodeConfig = entityConfig({
  entity: {} as ArrangementResource,
  collection: 'arrangementResourceByCode',
  selectId: (e: ArrangementResource) => e.streamMetadata?.code as string,
});

/**
 * Arrangement resources saved by the contentIdentifier
 */
const arrangementResourcesConfig = entityConfig({
  entity: {} as ArrangementResource,
  collection: 'arrangementResources',
  selectId: (e: ArrangementResource) => e.contentIdentifier,
});

const referencesResourcesConfig = entityConfig({
  entity: {} as Resource,
  collection: 'referencesResources',
  selectId: (e: Resource) => e.contentIdentifier,
});

const clustersConfig = entityConfig({
  entity: {} as Cluster,
  collection: 'clusters',
  selectId: (e: Cluster) => e.code,
});

export const ContentStore = signalStore(
  { providedIn: 'root' },
  withEntities(resourcesConfig),
  withEntities(contentConfig),
  withEntities(methodsConfig),
  withEntities(clustersWithArrangementsConfig),
  withEntities(arrangementsConfig),
  withEntities(arrangementResourceByCodeConfig),
  withEntities(arrangementResourcesConfig),
  withEntities(referencesResourcesConfig),
  withEntities(clustersConfig),
  withRequestStatus(),
  withComputed(({ methodsEntities, contentEntities }) => ({
    hasNt2Method: computed(() =>
      methodsEntities().some(
        (m) => m.subject === NT2_SUBJECT || m.subjects?.includes(NT2_SUBJECT),
      ),
    ),
    contentRecord: computed(
      () =>
        keyBy(contentEntities(), 'contentIdentifier') as Record<
          string,
          ContentEntry
        >,
    ),
  })),
  withMethods((store, contentService = inject(ContentService)) => {
    let contentParser: ContentParser | undefined;
    let methodsFetched = false;

    // Derives `clustersWithArrangements` from the current clusters/arrangements
    // entities. Mirrors the previous Elf facade's #setClustersWithArrangements.
    function deriveClustersWithArrangements(): void {
      const clusters = store.clustersEntities();
      const arrangements = store.arrangementsEntities();

      const userDefinedArrangements: Array<Arrangement> = [];

      const arrangementsMap = arrangements.reduce(
        (acc, arrangement) => {
          if (arrangement.type === 'editorial') {
            acc[arrangement.code] = arrangement;
          } else {
            userDefinedArrangements.push(arrangement);
          }

          return acc;
        },
        {} as Record<string, Arrangement>,
      );

      const mapped = clusters.map((cluster) => {
        const clusterArrangements = cluster.arrangementCodes
          .filter((code) => !!arrangementsMap[code])
          .map((code) => arrangementsMap[code]);

        return {
          ...cluster,
          arrangements: clusterArrangements,
        } as ClusterWithArrangements;
      });

      const userDefinedArrangementsMap = userDefinedArrangements.reduce(
        (acc, arrangement) => {
          const methodSlug = arrangement.methodObject.slug;
          (acc[methodSlug] = acc[methodSlug] ?? []).push(arrangement);

          return acc;
        },
        {} as Record<string, Array<Arrangement>>,
      );

      Object.entries(userDefinedArrangementsMap).forEach(
        ([methodSlug, userDefinedArrangementsForMethod]) => {
          mapped.unshift({
            code: `user-defined-${methodSlug}`,
            name: translate('onboarding.preference.custom_arrangements.title'),
            methodName: userDefinedArrangementsForMethod[0].methodObject.name,
            methodSlug: userDefinedArrangementsForMethod[0].methodObject.slug,
            arrangementCodes: userDefinedArrangementsForMethod.map(
              (a: Arrangement) => a.code,
            ),
            arrangements: userDefinedArrangementsForMethod,
          } as ClusterWithArrangements);
        },
      );

      patchState(store, setAllEntities(mapped, clustersWithArrangementsConfig));
    }

    function upsertArrangementResources(
      resources: Array<ArrangementResource>,
    ): void {
      patchState(
        store,
        upsertEntities(
          resources.map(
            (resource) =>
              ({
                ...resource,
                code: resource.streamMetadata?.code,
              }) as ArrangementResource,
          ),
          arrangementResourceByCodeConfig,
        ),
      );

      const entities = flattenResources(resources);
      patchState(store, upsertEntities(entities, arrangementResourcesConfig));
    }

    function upsertResources(resources: Array<Resource>): void {
      const entities = flattenResources(resources);

      entities.forEach((entity) => {
        const parentIdentifier =
          entity.parentIdentifier || entity.parent?.contentIdentifier;

        patchState(
          store,
          upsertEntity(
            { ...entity, parentIdentifier } as Resource,
            resourcesConfig,
          ),
        );
      });
    }

    return {
      enableLocalParsing(): void {
        contentParser = new ContentParser('assets/content/local-index.xsl');
      },

      methodBySlug(slug: string) {
        return computed(() => store.methodsEntityMap()[slug]);
      },

      arrangementByCode(code: string): Signal<Arrangement | undefined> {
        return computed(() => store.arrangementsEntityMap()[code]);
      },

      resourceById(contentIdentifier: string) {
        return computed(() => store.resourcesEntityMap()[contentIdentifier]);
      },

      arrangementResourceByCode(arrangementCode: string) {
        return computed(
          () => store.arrangementResourceByCodeEntityMap()[arrangementCode],
        );
      },

      arrangementResourceById(contentIdentifier: string) {
        return computed(
          () => store.arrangementResourcesEntityMap()[contentIdentifier],
        );
      },

      referencesResourceById(contentIdentifier: string) {
        return computed(
          () => store.referencesResourcesEntityMap()[contentIdentifier],
        );
      },

      contentById(contentIdentifier: string) {
        return computed(() => store.contentEntityMap()[contentIdentifier]);
      },

      questionsByParentId(contentIdentifier: string) {
        return computed(() =>
          collectQuestionsByParentId(
            contentIdentifier,
            store.resourcesEntities(),
          ),
        );
      },

      navigableChildrenByParentId(contentIdentifier: string) {
        return computed(() =>
          store
            .resourcesEntities()
            .filter(
              (e) =>
                e.parentIdentifier === contentIdentifier &&
                (e.digitalResourceType === 'question' ||
                  e.digitalResourceType === 'questionGroup'),
            ),
        );
      },

      clustersWithArrangementsByMethodSlug(methodSlug: string) {
        return computed(() =>
          store
            .clustersWithArrangementsEntities()
            .filter((e) => e.methodSlug === methodSlug),
        );
      },

      methodClusterArrangementsBySlug(methodSlug: string) {
        return computed(() => {
          const method = store.methodsEntityMap()[methodSlug];
          const clusterArrangements = store
            .clustersWithArrangementsEntities()
            .filter((e) => e.methodSlug === methodSlug);

          if (!method || clusterArrangements.length === 0) {
            return undefined;
          }

          return { method, clusterArrangements };
        });
      },

      async fetchMethodsIfNotFetched(): Promise<void> {
        if (!methodsFetched) {
          await this.fetchMethods();
        }
      },

      async fetchMethods(): Promise<void> {
        methodsFetched = true;
        const methods = await firstValueFrom(contentService.fetchMethods());
        patchState(store, upsertEntities(methods, methodsConfig));
      },

      async fetchMethod(methodSlug: string): Promise<MethodDetail> {
        const method = await firstValueFrom(
          contentService.fetchMethodBySlug(methodSlug),
        );
        patchState(store, upsertEntity(method as Method, methodsConfig));

        return method;
      },

      async fetchClusters(): Promise<void> {
        const clusters = await firstValueFrom(contentService.fetchClusters());
        patchState(store, setAllEntities(clusters, clustersConfig));
        deriveClustersWithArrangements();
      },

      async fetchArrangement(code: string): Promise<Arrangement> {
        const arrangement = await firstValueFrom(
          contentService.fetchArrangementByCode(code),
        );
        patchState(store, upsertEntity(arrangement, arrangementsConfig));
        deriveClustersWithArrangements();

        return arrangement;
      },

      async fetchArrangementsByType(
        type: ArrangementType,
      ): Promise<Array<Arrangement>> {
        const arrangements = await firstValueFrom(
          contentService.fetchPublishedArrangementsOfType(type),
        );

        patchState(
          store,
          removeEntities(
            (arrangement) => arrangement.type === type,
            arrangementsConfig,
          ),
          upsertEntities(arrangements, arrangementsConfig),
        );
        deriveClustersWithArrangements();

        return arrangements;
      },

      async fetchArrangementResource(
        arrangementCode: string,
        depth = 4,
        includeTypes: Array<ResourceType> = [],
      ): Promise<Array<ArrangementResource>> {
        const resources = await firstValueFrom(
          contentService.fetchResourceByArrangementCode<ArrangementResource>(
            arrangementCode,
            '',
            depth,
            {
              include: [
                'thedContentIdentifier',
                'alexContentIdentifier',
                'streamMetadata.method',
                'navigationMetadata.audience',
                'navigationMetadata.backgroundImage',
                'navigationMetadata.backgroundImageCopyright',
                'navigationMetadata.hasTerms',
                'navigationMetadata.hasSources',
                'navigationMetadata.hasResultables',
                'navigationMetadata.links',
                'navigationMetadata.contentType',
                'learningMetadata.language',
                'learningMetadata.typicalLearningTime',
                'instructorAssets',
              ],
            },
            {
              include: ['module', 'chapter', 'paragraph', ...includeTypes],
            },
          ),
        );

        upsertArrangementResources(resources);

        return resources;
      },

      async fetchArrangementResourceIfMissing(
        arrangementCode: string,
        depth = 4,
        includeTypes: Array<ResourceType> = [],
      ): Promise<ArrangementResource | undefined> {
        const existing =
          store.arrangementResourceByCodeEntityMap()[arrangementCode];

        if (existing) {
          return existing;
        }

        const resources = await this.fetchArrangementResource(
          arrangementCode,
          depth,
          includeTypes,
        );

        return resources[0];
      },

      async fetchResources(
        arrangementCode: string,
        contentIdentifier: string,
        depth = 2,
        disabled = true,
        types:
          | { include?: Array<ResourceType>; exclude?: Array<ResourceType> }
          | undefined = undefined,
      ): Promise<Array<Resource>> {
        const resources = await firstValueFrom(
          contentService.fetchResourceByArrangementCode<Resource>(
            arrangementCode,
            contentIdentifier,
            depth,
            {
              include: [
                'thedContentIdentifier',
                'alexContentIdentifier',
                'terms',
                'streamMetadata.method',
                'navigationMetadata.scoreType',
                'navigationMetadata.questionType',
                'navigationMetadata.hasTheory',
                'navigationMetadata.hasTerms',
                'navigationMetadata.hasSources',
                'navigationMetadata.hasResultables',
                'navigationMetadata.backgroundImage',
                'navigationMetadata.backgroundImageCopyright',
                'navigationMetadata.audience',
                'navigationMetadata.links',
                'navigationMetadata.contentType',
                'editionMetadata.glueContent',
                'editionMetadata.glueContentTitle',
                'editionMetadata.assessment',
                'learningMetadata.language',
                'learningMetadata.isAdaptive',
                'learningMetadata.typicalLearningTime',
                'smartMetadata.allLearningGoals',
                'instructorAssets',
                'wordLists',
              ],
            },
            types,
            disabled,
          ),
        );

        upsertResources(resources);

        return resources;
      },

      async fetchReferenceResources(
        arrangementCode: string,
        contentIdentifier = '',
        depth = 6,
      ): Promise<Array<Resource>> {
        const resources = await firstValueFrom(
          contentService.fetchResourceByArrangementCode<Resource>(
            arrangementCode,
            contentIdentifier,
            depth,
            {
              include: [
                'thedContentIdentifier',
                'alexContentIdentifier',
                'terms',
                'navigationMetadata.backgroundImage',
                'navigationMetadata.backgroundImageCopyright',
                'navigationMetadata.audience',
                'navigationMetadata.hasSources',
                'navigationMetadata.hasTerms',
                'wordLists',
                'learningMetadata.language',
              ],
            },
            {
              include: [
                'chapter',
                'itemMap',
                'module',
                'paragraph',
                'sourceMap',
                'source',
                'introduction',
                'conclusion',
                'subparagraph',
              ],
            },
          ),
        );

        patchState(store, upsertEntities(resources, referencesResourcesConfig));

        return resources;
      },

      async fetchContent(
        arrangementCode: string,
        contentIdentifier: string,
        includeSourceMaterial = false,
      ): Promise<string> {
        const parser = contentParser;
        const { fetchContentById, fetchRawContentById } = contentService;
        const fetch = parser ? fetchRawContentById : fetchContentById;

        const rawContent = await firstValueFrom(
          fetch(arrangementCode, contentIdentifier, includeSourceMaterial),
        );
        const content = parser?.parseFromString(rawContent) || rawContent;

        patchState(
          store,
          upsertEntity({ contentIdentifier, content }, contentConfig),
        );

        return content;
      },

      async fetchSourceContent(
        arrangementCode: string,
        contentIdentifier: string,
      ): Promise<string> {
        const parser = contentParser;
        const { fetchContentById, fetchRawContentById } = contentService;
        const fetch = parser ? fetchRawContentById : fetchContentById;

        const rawContent = await firstValueFrom(
          fetch(arrangementCode, contentIdentifier, true),
        );

        return parser?.parseFromString(rawContent) || rawContent;
      },

      async fetchRawContent(
        arrangementCode: string,
        contentIdentifier: string,
      ): Promise<string> {
        return firstValueFrom(
          contentService.fetchRawContentById(
            arrangementCode,
            contentIdentifier,
            true,
          ),
        );
      },

      fetchResourcesForMostRecentProgress(
        recentScores?: Array<RecentScoreItem>,
      ) {
        if (!recentScores || recentScores.length === 0) {
          return Promise.resolve([]);
        }

        if (recentScores.length === 1 && recentScores[0] === null) {
          return Promise.resolve([]);
        }

        let recentScoreItems = recentScores;
        if (!Array.isArray(recentScores)) {
          recentScoreItems = [recentScores];
        }

        const fields = {
          include: [
            'thedContentIdentifier',
            'alexContentIdentifier',
            'streamMetadata.method',
            'navigationMetadata.backgroundImage',
            'navigationMetadata.backgroundImageCopyright',
          ],
        };

        return firstValueFrom(
          contentService.fetchMultipleResources(
            recentScoreItems.map((score) => ({
              arrangementCode: score.arrangementCode,
              contentIdentifier: score.contentIdentifier,
            })),
            fields,
          ),
        );
      },

      async fetchContentByContentPath(
        streamCode: string,
        contentPath: string,
        depth?: number,
        digitalResourceTypes?: Array<ResourceType>,
      ): Promise<Array<ContentItem>> {
        const items = await firstValueFrom(
          contentService.fetchByContentPath(
            streamCode,
            contentPath,
            depth,
            digitalResourceTypes,
          ),
        );

        items.forEach((item) => {
          patchState(
            store,
            upsertEntity(
              { contentIdentifier: item.identifier, content: item.content },
              contentConfig,
            ),
          );
        });

        return items;
      },
    };
  }),
);
